import { getPath, types } from 'mobx-state-tree'
import type { OperationStatus } from '../models/common/OperationStatus'

export enum NotificationLevel {
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
    Success = 'success',
}

export const NotificationModel = types.model('NotificationModel')
    .props({
        message: types.string,
        level: types.enumeration('NotificationLevel', Object.values(NotificationLevel))
    })

export const NotificationStore = types.model('NotificationStore')
    .props({
        notifications: types.array(NotificationModel)
    })
    .volatile(() => ({
        _lastShownOperationStatusForTreePath: new Map<string, OperationStatus>()
    }))
    .actions((self) => ({
        showNotification(message: string, level: NotificationLevel) {
            self.notifications.push(
                NotificationModel.create({ message, level })
            )
        },
        showOperationStatusNotification(operationStatus: OperationStatus, message: string) {
            const statusTreePath = getPath(operationStatus)
            const lastShownOperationStatus = self._lastShownOperationStatusForTreePath.get(statusTreePath)

            // Compare (by reference) previous operationStatus instance for which we showed a notification with the new one, for this path in the mobx state tree (for example `usersStore/deleteStatus`).
            // If this is the same operationStatus object (as can be in case when returning to a page with some saveStatus, for example), no need to notify again.
            // Note that creating a new OperationStatus instance using `saveStatus = OperationInProgress()` is a new instance and hence will show a notification.
            if (operationStatus === lastShownOperationStatus) {
                return
            }
            self._lastShownOperationStatusForTreePath.set(statusTreePath, operationStatus)

            const notificationLevel =  
                operationStatus.isSuccess ? NotificationLevel.Success :
                operationStatus.error ? NotificationLevel.Error :
                NotificationLevel.Info

            self.notifications.push(NotificationModel.create({ message, level: notificationLevel }))
        },
    }))